// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo, useEffect, useRef, useState } from "react";
import { getSession } from "../utils/sessionstore";
import {
  addDashboardFeedback,
  addOrgs,
  refreshRequest,
  refreshSuccess,
  listBanner,
  listFavourites,
  listNotification,
  newslistSuccess,
  listWhitePapers,
  updateFeedbackStatus,
  listSubscriptions,
  listTrendings,
  setLandingLayout,
  listUserDetails,
  listRecents,
  logoutFailure,
  setDashboardPopUp,
  startIsLoading,
  floatingChatBotPop,
  setShowLaunchCampaign,
  setIsWebinarPopActive,
  showNpsPopup,
  setIsNpsPopupActive,
  hideNpsPopup,
  setSlidingCardsSalesContent,
  showTour,
} from "../store/auth/action";
import { refreshService } from "../services/refresh.service";
import { getRecentDashboards } from "../services/recent.service";
import { getAllClients } from "../services/hwai_user.service";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import NotFound from "../views/NotFound";
import { SESSION_EXPIRED_ERROR } from "../components/errors/AuthError";
import { logoutUser } from "../services/auth.service";
import usePostMessageListener from "../hooks/capturePostMessageHook";
import { logAmplitudeEvent, reIdentifyAmplitudeUserProperties, startSessionReplayTracking } from "../utils/amplitude/functions";
import amplitude, { initAmplitude } from "../utils/amplitude/initiate";
import usePageVisibility from "../hooks/pageVisibilityHook";
import { getOrCreateTabId } from "../utils/browserTab";
import { handleLiveItemLog } from "../utils/handleItem";
import { sendMessageToChild } from "../utils/amplitude/postMessage";
import environment from "../config/environment";
import { getProductLineFromDashboard } from "../utils/functions";
import { clearStorage } from "../utils/clearstore";
import { getSlidingCardsSalesContentAPI } from "../services/cards.service";

const PrivateRoute = ({ children }) => {
  const { subscriptions, userDetails, clientname, activedashboard, isVidePop, videoPopupContent, isSalesPopUp, lobbyStartTime, dashboardStartTime } = useSelector((state) => ({
    subscriptions: state.subscriptions,
    userDetails: state.userDetails,
    clientname: state.clientname,
    activedashboard: state.activedashboard,
    isVidePop: state.isVidePop,
    videoPopupContent: state.videoPopupContent,
    isSalesPopUp: state.isSalesPopUp,
    lobbyStartTime: state.lobbyStartTime,
    dashboardStartTime: state.dashboardStartTime
  }));

  // const [productLine, setProductLine] = useState("");

  const { clientID } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sessionId = getSession("session_id");
  const token = getSession("session_hwai");
  const hwai_user_token = getSession("session_hwai_user");
  const client_id = getSession("client_id");
  const pathParts = useLocation().pathname.split("/");
  const sessionIntervalRef = useRef(null);
  const isTabVisible = usePageVisibility();
  const location = useLocation();
  const { type } = useParams();

  const subscriptionActive = useSelector((state) => state.subscriptionActive);

  const liveIntervalRef = useRef(null);
  const idleIntervalRef = useRef(null);
  const idleTimerRef = useRef(null);
  const isIdleRef = useRef(false);
  const activityEvents = ['mousemove', 'keydown', 'scroll', 'touchstart'];

  const startLivePulse = () => {
    if (liveIntervalRef.current) return;

    relogActiveTab(); // First "Page is Live"
    liveIntervalRef.current = setInterval(() => {
      relogActiveTab();
    }, 10000);
  };

  const startIdlePulse = () => {
    if (idleIntervalRef.current) return;

    relogIdleTab(); // First "Page is Idle"
    idleIntervalRef.current = setInterval(() => {
      relogIdleTab();
    }, 10000);
  };

  const stopIdlePulse = () => {
    if (idleIntervalRef.current) {
      clearInterval(idleIntervalRef.current);
      idleIntervalRef.current = null;
    }
  };

  const resetIdleCheck = () => {
    // User is active again
    if (isIdleRef.current) {
      isIdleRef.current = false;
      stopIdlePulse();
    }
    

    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      isIdleRef.current = true;
      startIdlePulse();
    }, 60000); // 1 minute idle threshold
  };

  const isValidMongoId = (id) => {
    return id?.length === 24 && /^[a-f\d]{24}$/i.test(id);
  };

  async function logOut() {
    try {
      dispatch(startIsLoading());
      await logoutUser({ activedashboard, type, token, session_id: sessionId, userDetails, clientname, client_id, lobbyStartTime, dashboardStartTime, endTime: new Date() });
      navigate("/login");
    } catch (err) {
      dispatch(logoutFailure(SESSION_EXPIRED_ERROR));
    }
  };

  const onRefresh = async (token, hwai_user_token, clientid) => {
    try {
      const payload = { token, clientid, sessionId };
      // console.log("Payload", payload);
      dispatch(refreshRequest(payload));

      let orglist = [];
      if (hwai_user_token) {
        const response = await getAllClients();
        orglist = response?.clients;
        // console.log("Org List", orglist);
        dispatch(addOrgs(orglist));
      }

      const response = await refreshService(payload);
      // console.log("Response", response);
      const { data } = response?.data;
      // console.log("Refresh Data", data);
      const userData = data?.user;
      const sessionData = response?.data?.session;

      const slidingCardsSalesContent = await getSlidingCardsSalesContentAPI({clientId: userData?.clientId?._id});
      dispatch(setSlidingCardsSalesContent(slidingCardsSalesContent));

      dispatch(addDashboardFeedback(data?.dashboardfeedback));
      dispatch(
        refreshSuccess(
          sessionData,
          userData?.clientId?.logo,
          userData?.clientId,
          orglist,
          {
            userSQLId: userData?.userSQLId,
            trendingConfig: userData?.trendingConfig,
            userName: userData?.userName,
            email: userData?.email,
            firstName: userData?.firstName,
            lastName: userData?.lastName,
            address: userData?.address,
            mobile: userData?.mobile,
            about: userData?.about,
            permissions: userData?.permissions,
            jobTitle: userData?.jobTitle,
            department: userData?.department,
            userId: userData?._id,
            clientId: userData?.clientId?._id,
          }
        )
      );

      dispatch(listNotification(data?.notification));
      dispatch(listBanner(userData?.bannerId || {}));
      dispatch(listSubscriptions(data?.subscriptions));
      dispatch(listFavourites(data?.favourites?.slice(0, 12)));
      dispatch(listWhitePapers(userData?.whitepaperIds));
      dispatch(listTrendings(data?.trendings));
      dispatch(setLandingLayout(data?.landingLayout || null));
      dispatch(listUserDetails(userData));
      dispatch(newslistSuccess(data?.newsinfo));
      dispatch(updateFeedbackStatus(data?.feedback));
      // Reverted the TEMP local-dev-only override (forced `showTour(true)` on
      // every refresh) now that tour work is far enough along - it was causing
      // the welcome popup to keep reappearing on reload even right after the
      // backend's showTour flag had already been flipped to false. Back to the
      // real backend-driven flag, matching Login.jsx/HwaiLogin.jsx.
      dispatch(showTour(userData?.showTour));
      dispatch(setDashboardPopUp(userData?.clientId?.config?.dashboardpopup));
      dispatch(floatingChatBotPop(userData?.clientId?.config?.isFloatingBtnEnabled));
      dispatch(setShowLaunchCampaign(userData?.clientId?.config?.showLaunchCampaign));
    } catch (err) {
      console.error("Error in refresh:", err?.response?.data);
      if (err?.response?.data === "Server Error: Cannot read properties of null (reading '_id')") {
        amplitude.reset();
        clearStorage();
      }
      // dispatch(refreshFailure(err.message));
    }
  };

  useEffect(() => {
    const sessionCheck = () => {
      if (!getSession("session_hwai") || !getSession("session_id")) {
        logOut();
      }
    };

    sessionIntervalRef.current = setInterval(sessionCheck, 1000);
    return () => clearInterval(sessionIntervalRef.current);
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/login");
    } else if (!subscriptions?.length) {
      onRefresh(token, hwai_user_token, clientID || client_id);
    }
  }, [token, hwai_user_token]);

  useEffect(() => {
    if (
      subscriptionActive === false &&
      pathParts[1] &&
      userDetails?.permissions !== "HWAI_ADMIN"
    ) {
      navigate("/"); //
    }
  }, [subscriptionActive]);

  useEffect(() => {
    if (clientID && !isValidMongoId(clientID)) {
      const localClientId = getSession("client_id"); // Fetch from local storage or session
      if (localClientId && userDetails.permissions === "HWAI_ADMIN") {
        onRefresh(token, hwai_user_token, localClientId);
        navigate(`/${localClientId}`);
      }
    }
  }, [clientID]);

  useEffect(() => {
    getOrCreateTabId();
  }, [])

  // Recents are per (user, client), so refetch when the active client changes.
  useEffect(() => {
    const recentsClientId = clientID || client_id;
    if (!token || !recentsClientId || !isValidMongoId(recentsClientId)) return;

    let cancelled = false;
    (async () => {
      try {
        const data = await getRecentDashboards(token, recentsClientId);
        if (!cancelled) dispatch(listRecents(data?.recents || []));
      } catch (err) {
        // Recents are non-critical; leave the list empty on failure.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, clientID, client_id]);

  const intervalRef = useRef(null)

  const paramsRef = useRef({
    activedashboard,
    videoPopupContent,
    clientID,
    clientname,
    type,
    isVidePop,
    isSalesPopUp,
    pathname: location.pathname,
  })

  useEffect(() => {
    paramsRef.current = {
      activedashboard,
      videoPopupContent,
      clientID,
      clientname,
      type,
      isVidePop,
      isSalesPopUp,
      pathname: location.pathname,
    }
  })

  const reLogItem = async (pulseType) => {
    const {
      activedashboard: dash,
      videoPopupContent: videoDash,
      clientID: client,
      clientname: cname,
      type: triggerType,
      isVidePop: vidPop,
      isSalesPopUp: salePop,
      pathname,
    } = paramsRef.current

    const trackedDashboard =
      vidPop && Object.keys(videoDash || {}).length > 0 ? videoDash : dash;
    const trackedTriggerType =
      trackedDashboard?.triggerType ?? triggerType ?? dash?.triggerType ?? "UNKNOWN_T";

    if (pathname.includes("dashboard") || vidPop || salePop) {
      await handleLiveItemLog({
        productLine: getProductLineFromDashboard(subscriptions, trackedDashboard),
        strictLogOnly: true,
        clientID: client ?? getSession("client_id"),
        userDetails,
        dashboard: trackedDashboard,
        clientName: cname,
        triggerType: trackedTriggerType,
        pathname,
        dispatch,
        navigate,
        pulseType,
      })
    }
    else if (pathname.includes("profile")) {
    }
    else {
    }
  }

  const relogActiveTab = async () => await reLogItem("default")
  const relogIdleTab = async () => await reLogItem("Idle")

  const switchSessionReplayToTechDashboard = async ({ keyValueObject }) => {
    sendMessageToChild("RESPONSE", {
      for: "LOCAL_STORAGE",
      lobbyStorage: { ...keyValueObject, environment, ...userDetails,dashboardStartTime },
    })
  }

  useEffect(() => {
    const { pathname } = paramsRef.current;

    // const isDashboardPath = /^\/[^\/]*\/dashboard\/[^\/]+(\/[^\/]+)?$/.test(pathname);
    const isDashboardPath = pathname.includes("/dashboard");

    const setup = async () => {
      if (!isDashboardPath || !isTabVisible) return;

      await initAmplitude(userDetails?.permissions, userDetails?.email);
      await reIdentifyAmplitudeUserProperties(amplitude, userDetails);

      if (pathname.includes("dashboard")) {
        switchSessionReplayToTechDashboard({
          keyValueObject: {
            amplitudeDeviceId: localStorage.getItem("amplitudeDeviceId"),
          },
        });
      } else {
        await startSessionReplayTracking(amplitude);
      }

      setTimeout(() => {
        relogActiveTab(); // First Page is Live
      }, 1000);

      const ms = 10 * 1000;
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(reLogItem, ms); // Continuous Page is Live
    };

    setup();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [location.pathname, isTabVisible]);


  useEffect(() => {
    if (!isTabVisible) return;

    activityEvents.forEach(event =>
      window.addEventListener(event, resetIdleCheck)
    );

    resetIdleCheck(); // kick off idle check on load

    return () => {
      activityEvents.forEach(event =>
        window.removeEventListener(event, resetIdleCheck)
      );
      stopIdlePulse();
      clearTimeout(idleTimerRef.current);
    };
  }, [isTabVisible]);
  usePostMessageListener(async (type, payload, event) => {
    if (type === "LOGS") {
      console.log("Received logs from child:", payload);
  
      let processedPayload = payload;
  
      // Only process if either key exists
      if (payload?.loadedIn !== undefined || payload?.timeTaken !== undefined) {
        processedPayload = { ...payload }; // shallow copy
  
        ["loadedIn", "timeTaken"].forEach((key) => {
          if (processedPayload[key] !== undefined && typeof processedPayload[key] === "string") {
            const numValue = parseFloat(processedPayload[key].replace(/[^\d.]/g, ""));
            if (!isNaN(numValue)) {
              processedPayload[key] = numValue;
            }
          }
        });
      }

      if (processedPayload.loadedIn !== undefined) {
  processedPayload.timeTaken = processedPayload.timeTaken ?? processedPayload.loadedIn;
  delete processedPayload.loadedIn;
}

  
      logAmplitudeEvent(amplitude, "Page Interacted", {
        productLine: getProductLineFromDashboard(subscriptions, activedashboard),
        clientName: clientname ?? "",
        clientId: clientID ?? getSession("client_id"),
        dashboardName: activedashboard?.dashboardname ?? "",
        standardDashboardName: activedashboard?.standarddashboardId?.standardName ?? "",
        dashboardId: activedashboard?._id ?? "",
        triggerType: processedPayload?.triggerType ?? null,
        targetType: processedPayload?.targetType ?? null,
        eventTimestamp: new Date().toLocaleString('en-US', { timeZone: 'UTC' }),
        ...processedPayload
      });
    } else if (type === "IDLE_PULSE") {
      // relogIdleTab()
    } else if (type === "REQUEST") {
      if (payload?.for === "LOCAL_STORAGE") {
        const keyValueObject = Object.fromEntries(
          (payload?.keys || []).map((key) => [key, localStorage.getItem(key)])
        );
        switchSessionReplayToTechDashboard({ keyValueObject })
      }
    }
  });

  return clientID && !isValidMongoId(clientID) ? <NotFound /> : children;
};

export default memo(PrivateRoute);
